import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { useGlobalFilters, FILTER_LABELS, type GlobalFilters } from './GlobalFilterContext';
import { API_BASE } from '../../config';

// ============================================================
// GLOBAL FILTER BAR — Option A: Inline Filter Bar below nav
// Persistent strip showing active filters + expandable panel
// ============================================================
export default function GlobalFilterBar() {
  const { T } = useTheme();
  const {
    filters,
    setFilter,
    clearAll,
    clearFilter,
    applicableFilters,
    hasActiveFilters,
    activeFilterCount,
    otherPageFilterCount,
    filterOptions,
  } = useGlobalFilters();

  const [expanded, setExpanded] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [mfrSearch, setMfrSearch] = useState('');
  const [mfrSuggestions, setMfrSuggestions] = useState<string[]>([]);
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState(false);
  const [mfrLoading, setMfrLoading] = useState(false);
  const mfrInputRef = useRef<HTMLInputElement>(null);
  const mfrDropdownRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Debounced manufacturer search
  const mfrSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchMfrSuggestions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setMfrSuggestions([]);
      setMfrDropdownOpen(false);
      return;
    }
    setMfrLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/metasupply/manufacturers/names?search=${encodeURIComponent(query)}&limit=20`,
        { credentials: 'include' },
      );
      if (res.ok) {
        const data = await res.json();
        setMfrSuggestions(data.manufacturers || []);
        setMfrDropdownOpen(true);
      }
    } catch { /* silently fail */ }
    finally { setMfrLoading(false); }
  }, []);

  const handleMfrSearchChange = useCallback((value: string) => {
    setMfrSearch(value);
    if (mfrSearchTimer.current) clearTimeout(mfrSearchTimer.current);
    mfrSearchTimer.current = setTimeout(() => fetchMfrSuggestions(value), 250);
  }, [fetchMfrSuggestions]);

  // Close manufacturer dropdown on outside click
  useEffect(() => {
    if (!mfrDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        mfrDropdownRef.current && !mfrDropdownRef.current.contains(e.target as Node) &&
        mfrInputRef.current && !mfrInputRef.current.contains(e.target as Node)
      ) {
        setMfrDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mfrDropdownOpen]);

  // Close panel on outside click
  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  // If no filters are applicable on this page, show minimal bar
  if (applicableFilters.length === 0) {
    if (otherPageFilterCount === 0) return null;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 40px',
        background: T.offWhite,
        borderBottom: `1px solid ${T.border}`,
        fontSize: 12, color: T.textLight,
        flexShrink: 0,
        maxWidth: 1280, margin: '0 auto',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 10px', borderRadius: 12,
          background: `${T.gold}15`, color: T.gold,
          fontSize: 11, fontWeight: 600,
        }}>
          {otherPageFilterCount} filter{otherPageFilterCount !== 1 ? 's' : ''} active on other pages
        </span>
      </div>
    );
  }

  // Build active pill descriptors for applicable filters
  const activePills: { key: keyof GlobalFilters; label: string; values: string }[] = [];
  for (const key of applicableFilters) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      activePills.push({
        key,
        label: FILTER_LABELS[key],
        values: value.length <= 2 ? value.join(', ') : `${value.length} selected`,
      });
    } else if (typeof value === 'string' && value) {
      const displayMap: Record<string, Record<string, string>> = {
        shortageStatus: { active: 'Active', resolved: 'Resolved', none: 'None' },
        baaEligible: { yes: 'Eligible', no: 'Not Eligible' },
        ccpFlag: { yes: 'CCP Flagged', no: 'Not Flagged' },
      };
      const display = displayMap[key]?.[value] || value;
      activePills.push({
        key,
        label: FILTER_LABELS[key],
        values: display,
      });
    }
  }

  // Filtered countries for search
  const filteredCountries = countrySearch
    ? filterOptions.countries.filter(c => c.toLowerCase().includes(countrySearch.toLowerCase()))
    : filterOptions.countries.slice(0, 20);

  // ── Render helpers ──────────────────────────────────────────

  const renderCheckboxGroup = (
    filterKey: keyof GlobalFilters,
    options: { value: string; label: string }[],
  ) => {
    const current = filters[filterKey] as string[];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map(opt => {
          const checked = current.includes(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => {
                const next = checked
                  ? current.filter(v => v !== opt.value)
                  : [...current, opt.value];
                setFilter(filterKey, next as never);
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 16,
                border: `1px solid ${checked ? T.navy : T.border}`,
                background: checked ? `${T.navy}0A` : T.white,
                color: checked ? T.navy : T.textMid,
                fontSize: 12, fontWeight: checked ? 700 : 500,
                cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: 'inherit',
              }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 14, height: 14, borderRadius: 3,
                border: `1.5px solid ${checked ? T.navy : T.border}`,
                background: checked ? T.navy : 'transparent',
                fontSize: 9, color: T.white, lineHeight: 1,
              }}>
                {checked ? '\u2713' : ''}
              </span>
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderSelectFilter = (
    filterKey: 'shortageStatus' | 'baaEligible' | 'ccpFlag',
    options: { value: string; label: string }[],
  ) => {
    const current = filters[filterKey];
    return (
      <select
        value={current}
        onChange={e => setFilter(filterKey, e.target.value)}
        style={{
          padding: '6px 10px', borderRadius: T.radiusSm,
          border: `1px solid ${current ? T.navy : T.border}`,
          background: current ? `${T.navy}0A` : T.white,
          color: current ? T.navy : T.textMid,
          fontSize: 12, fontWeight: current ? 600 : 400,
          cursor: 'pointer', outline: 'none',
          minWidth: 120, fontFamily: 'inherit',
        }}
      >
        <option value="">All</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  };

  const renderCountryMultiSelect = () => {
    const selected = filters.country;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input
          type="text"
          placeholder="Search countries..."
          value={countrySearch}
          onChange={e => setCountrySearch(e.target.value)}
          style={{
            padding: '5px 10px', borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`, fontSize: 12,
            color: T.textDark, outline: 'none', width: 180,
            fontFamily: 'inherit',
          }}
        />
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4,
          maxHeight: 120, overflowY: 'auto',
        }}>
          {filteredCountries.map(country => {
            const checked = selected.includes(country);
            return (
              <button
                key={country}
                onClick={() => {
                  const next = checked
                    ? selected.filter(c => c !== country)
                    : [...selected, country];
                  setFilter('country', next);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 12,
                  border: `1px solid ${checked ? T.navy : T.border}`,
                  background: checked ? `${T.navy}0A` : T.white,
                  color: checked ? T.navy : T.textMid,
                  fontSize: 11, fontWeight: checked ? 600 : 400,
                  cursor: 'pointer', transition: 'all 0.12s',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 12, height: 12, borderRadius: 2,
                  border: `1.5px solid ${checked ? T.navy : T.border}`,
                  background: checked ? T.navy : 'transparent',
                  fontSize: 8, color: T.white, lineHeight: 1,
                }}>
                  {checked ? '\u2713' : ''}
                </span>
                {country}
              </button>
            );
          })}
          {filteredCountries.length === 0 && (
            <span style={{ fontSize: 11, color: T.textLight, padding: '4px 0' }}>
              No countries match &quot;{countrySearch}&quot;
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderManufacturerMultiSelect = () => {
    const selected = filters.manufacturer;
    // Filter out already-selected from suggestions
    const filteredSuggestions = mfrSuggestions.filter(s => !selected.includes(s));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ position: 'relative' }}>
          <input
            ref={mfrInputRef}
            type="text"
            placeholder="Search manufacturers..."
            value={mfrSearch}
            onChange={e => handleMfrSearchChange(e.target.value)}
            onFocus={() => { if (mfrSearch.trim()) fetchMfrSuggestions(mfrSearch); }}
            onKeyDown={e => {
              if (e.key === 'Escape') setMfrDropdownOpen(false);
              if (e.key === 'Enter' && filteredSuggestions.length > 0) {
                setFilter('manufacturer', [...selected, filteredSuggestions[0]]);
                setMfrSearch('');
                setMfrDropdownOpen(false);
              }
            }}
            style={{
              padding: '5px 10px', borderRadius: T.radiusSm,
              border: `1px solid ${mfrDropdownOpen ? T.navy : T.border}`, fontSize: 12,
              color: T.textDark, outline: 'none', width: 220,
              fontFamily: 'inherit',
            }}
          />
          {mfrLoading && (
            <span style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              fontSize: 10, color: T.textLight,
            }}>...</span>
          )}
          {mfrDropdownOpen && filteredSuggestions.length > 0 && (
            <div
              ref={mfrDropdownRef}
              style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radiusSm, boxShadow: T.shadowMd,
                maxHeight: 180, overflowY: 'auto', zIndex: 100,
                marginTop: 2,
              }}
            >
              {filteredSuggestions.map(name => (
                <button
                  key={name}
                  onClick={() => {
                    setFilter('manufacturer', [...selected, name]);
                    setMfrSearch('');
                    setMfrDropdownOpen(false);
                  }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 10px', border: 'none',
                    background: 'transparent', color: T.textDark,
                    fontSize: 12, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => { (e.target as HTMLElement).style.background = `${T.navy}08`; }}
                  onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent'; }}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          {mfrDropdownOpen && mfrSearch.trim() && filteredSuggestions.length === 0 && !mfrLoading && (
            <div
              ref={mfrDropdownRef}
              style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radiusSm, boxShadow: T.shadowMd,
                padding: '8px 10px', marginTop: 2,
                fontSize: 11, color: T.textLight,
              }}
            >
              No manufacturers found
            </div>
          )}
        </div>
        {selected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {selected.map(mfr => (
              <span
                key={mfr}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 12,
                  background: `${T.navy}0A`, border: `1px solid ${T.navy}30`,
                  fontSize: 11, color: T.navy, fontWeight: 600,
                }}
              >
                {mfr}
                <button
                  onClick={() => setFilter('manufacturer', selected.filter(m => m !== mfr))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 14, height: 14, borderRadius: '50%',
                    border: 'none', background: `${T.navy}20`,
                    color: T.navy, fontSize: 10, cursor: 'pointer',
                    padding: 0, lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Filter control definitions ──────────────────────────────
  const filterControls: Record<keyof GlobalFilters, () => React.ReactNode> = {
    cooConfidence: () => renderCheckboxGroup('cooConfidence', [
      { value: 'HIGH', label: 'High' },
      { value: 'MODERATE', label: 'Moderate' },
      { value: 'LOW', label: 'Low' },
    ]),
    country: renderCountryMultiSelect,
    riskLevel: () => renderCheckboxGroup('riskLevel', [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ]),
    manufacturer: renderManufacturerMultiSelect,
    shortageStatus: () => renderSelectFilter('shortageStatus', [
      { value: 'active', label: 'Active' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'none', label: 'None' },
    ]),
    baaEligible: () => renderSelectFilter('baaEligible', [
      { value: 'yes', label: 'Eligible' },
      { value: 'no', label: 'Not Eligible' },
    ]),
    ccpFlag: () => renderSelectFilter('ccpFlag', [
      { value: 'yes', label: 'CCP Flagged' },
      { value: 'no', label: 'Not Flagged' },
    ]),
  };

  // ── Main render ─────────────────────────────────────────────
  return (
    <div ref={panelRef} style={{ flexShrink: 0, position: 'relative', zIndex: 90 }}>
      {/* Collapsed bar */}
      <div style={{
        background: T.offWhite,
        borderBottom: `1px solid ${T.border}`,
      }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 40px',
        flexWrap: 'wrap',
        minHeight: 36,
        maxWidth: 1280, margin: '0 auto',
      }}>
        {/* Filters toggle button */}
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 6,
            border: `1px solid ${expanded ? T.navy : T.border}`,
            background: expanded ? `${T.navy}08` : T.white,
            color: expanded ? T.navy : T.textMid,
            fontSize: 12, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s',
            fontFamily: 'inherit',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="8" y1="12" x2="20" y2="12" />
            <line x1="12" y1="18" x2="20" y2="18" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 18, height: 18, borderRadius: '50%',
              background: T.navy, color: T.white,
              fontSize: 10, fontWeight: 800,
            }}>
              {activeFilterCount}
            </span>
          )}
          <svg
            width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
          >
            <path d="M2 4 L5 7 L8 4" />
          </svg>
        </button>

        {/* Active filter pills */}
        {activePills.map(pill => (
          <span
            key={pill.key}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 16,
              background: `${T.navy}0A`,
              border: `1px solid ${T.navy}25`,
              fontSize: 11, fontWeight: 600, color: T.navy,
            }}
          >
            <span style={{ color: T.textLight, fontWeight: 500 }}>{pill.label}:</span>
            {pill.values}
            <button
              onClick={() => clearFilter(pill.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 14, height: 14, borderRadius: '50%',
                border: 'none', background: `${T.navy}15`,
                color: T.navy, fontSize: 10, cursor: 'pointer',
                padding: 0, lineHeight: 1, fontFamily: 'inherit',
              }}
            >
              &times;
            </button>
          </span>
        ))}

        {/* Other page filters indicator */}
        {otherPageFilterCount > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 10px', borderRadius: 12,
            background: `${T.gold}12`, color: T.gold,
            fontSize: 11, fontWeight: 600,
          }}>
            {otherPageFilterCount} filter{otherPageFilterCount !== 1 ? 's' : ''} active on other pages
          </span>
        )}

        {/* Clear all */}
        {hasActiveFilters && (
          <button
            onClick={clearAll}
            style={{
              marginLeft: 'auto',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 6,
              border: `1px solid ${T.danger}40`,
              background: T.dangerBg,
              color: T.danger,
              fontSize: 11, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
              fontFamily: 'inherit',
            }}
          >
            Clear All
          </button>
        )}

        {/* No filters active message */}
        {!hasActiveFilters && (
          <span style={{ fontSize: 11, color: T.textLight }}>
            No filters active
          </span>
        )}
      </div>
      </div>

      {/* Expanded filter panel */}
      {expanded && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: T.white,
          borderBottom: `1px solid ${T.border}`,
          boxShadow: T.shadowMd,
          padding: '16px 40px',
          zIndex: 89,
          maxWidth: 1280, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 16,
        }}>
          {applicableFilters.map(key => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{
                fontSize: 11, fontWeight: 700,
                color: T.textLight,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
                {FILTER_LABELS[key]}
              </label>
              {filterControls[key]()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
